import { TrackedCard, KtStagingArea, KtShelf } from '../../../redux/api/kit-staging';

export interface ShelfSuggestion {
	area: KtStagingArea;
	shelf: KtShelf;
}

function firstFreeShelf(area: KtStagingArea): KtShelf | null {
	for (const r of area.racks) {
		for (const s of r.shelves) {
			if (s.is_active && !s.is_occupied) return s;
		}
	}
	return null;
}

function jsaForKit(card: TrackedCard, areas: KtStagingArea[]): KtStagingArea | null {
	const mgName = card.job?.machine_group?.name;
	if (!mgName) return null;
	return (
		areas.find(
			(a) => a.area_type === 'job_staging' && a.is_active && a.machine_groups.some((mg) => mg.name === mgName),
		) ?? null
	);
}

function msaForMaterial(areas: KtStagingArea[]): KtStagingArea | null {
	return areas.find((a) => a.area_type === 'material_staging' && a.is_active) ?? null;
}

/** Best-effort fallback: pick the first free shelf in ANY active staging
 *  area of the given type. Used when machine_group-based matching fails
 *  (no JSA serves this card's MG, or every shelf in that area is full).
 *  Lets the operator deliver without a manual rack picker. */
function firstFreeShelfAnywhere(areas: KtStagingArea[], areaType: 'jsa' | 'msa'): ShelfSuggestion | null {
	const filterType = areaType === 'jsa' ? 'job_staging' : 'material_staging';
	for (const a of areas) {
		if (a.area_type !== filterType || !a.is_active) continue;
		const s = firstFreeShelf(a);
		if (s) return { area: a, shelf: s };
	}
	return null;
}

/** Suggest a destination shelf for a card heading to a JSA or MSA area.
 *  JSA: first active job_staging area whose machine_groups contains the
 *  card's job.machine_group; first free shelf inside it. Falls back to
 *  first free shelf in ANY active JSA area when the MG-matched area
 *  is full or unknown.
 *  MSA: first active material_staging area; first free shelf inside it.
 *  Returns null only when no active area of that type exists or every
 *  shelf everywhere is full. */
export function suggestShelfForArea(
	card: TrackedCard,
	areas: KtStagingArea[],
	areaType: 'jsa' | 'msa',
): ShelfSuggestion | null {
	const primary = areaType === 'jsa' ? jsaForKit(card, areas) : msaForMaterial(areas);
	if (primary) {
		const shelf = firstFreeShelf(primary);
		if (shelf) return { area: primary, shelf };
	}
	// Fallback: any free shelf in any active area of the right type. The
	// operator can override later; meanwhile the card has a destination
	// so Cart never falls into the "PICK DESTINATION" rack prompt.
	return firstFreeShelfAnywhere(areas, areaType);
}
